import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowLeftRight, ClipboardCheck, Send } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { api, inr, critTone, actionTone } from "@/lib/mro";

const Panel = ({ title, sub, children, testid, className = "" }) => (
  <div className={`panel p-5 ${className}`} data-testid={testid}>
    <h3 className="font-display text-sm font-semibold">{title}</h3>
    {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    <div className="mt-3">{children}</div>
  </div>
);

const Field = ({ label, value }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-mono text-sm font-semibold">{value}</div>
  </div>
);

export default function Part360({ id }) {
  const [d, setD] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setD(null);
    api.get(`/part/${id}`).then((r) => setD(r.data)).catch(() => toast.error("Material not found"));
  }, [id]);

  const act = async (verb, label) => {
    await api.post("/recommendations/action", { material_id: id, action: verb, note: label });
    toast.success(`${label} logged`);
  };

  if (!d) return <div className="space-y-4"><Skeleton className="h-28 rounded-xl" /><Skeleton className="h-64 rounded-xl" /></div>;

  const m = d.material;
  const rec = d.recommendation;

  return (
    <div className="space-y-5" data-testid="part360-page">
      <Button variant="ghost" size="sm" data-testid="part360-back" onClick={() => navigate(-1)}>
        <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />Back
      </Button>

      <div className="panel p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Standardized material identity</div>
            <h1 className="mt-1 font-display text-xl font-extrabold tracking-tight sm:text-2xl" data-testid="part360-canonical">{d.identity.canonical_name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="font-mono text-[11px]">{m.material_code}</Badge>
              <Badge variant="outline" className="font-mono text-[11px]">{m.plant} · {m.storage_location}</Badge>
              <Badge variant="outline" className={critTone(m.criticality)}>Criticality {m.criticality}</Badge>
              <Badge variant="outline" className="border-violet-500/30 bg-violet-500/15 text-violet-500">
                Golden master: {d.identity.governance_status}{d.identity.confidence ? ` · ${d.identity.confidence}%` : ""}
              </Badge>
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Local description</div>
            <div className="font-mono text-sm">{m.description}</div>
            <div className="mt-1 font-mono text-xs text-muted-foreground">Legacy: {m.legacy_code}</div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-1.5" data-testid="part360-aliases">
          <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Aliases:</span>
          {d.identity.aliases.map((a) => <Badge key={a} variant="secondary" className="font-mono text-[11px]">{a}</Badge>)}
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-12">
        <Panel testid="part360-stock" className="lg:col-span-7" title="Current stock by plant / store" sub="Unrestricted, reserved, blocked and in-transit across the network">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/60">
                <tr className="text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2">Plant / store</th><th className="px-3 py-2">Local code</th>
                  <th className="px-3 py-2 text-right">Unrestr.</th><th className="px-3 py-2 text-right">Resv.</th>
                  <th className="px-3 py-2 text-right">Blocked</th><th className="px-3 py-2 text-right">Transit</th>
                  <th className="px-3 py-2 text-right">Value</th><th className="px-3 py-2 text-right">Last mvmt</th>
                </tr>
              </thead>
              <tbody>
                {d.stock_by_plant.map((s) => (
                  <tr key={s.material_code} className="border-t border-border">
                    <td className="px-3 py-2.5">{s.plant}<div className="font-mono text-[10px] text-muted-foreground">{s.storage_location} · {s.description}</div></td>
                    <td className="px-3 py-2.5 font-mono text-xs">{s.material_code}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular">{s.unrestricted}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular">{s.reserved}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular">{s.blocked}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular">{s.in_transit}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular">{inr(s.value)}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular">{s.months_since_movement} mo</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel testid="part360-consumption" className="lg:col-span-5" title="Monthly consumption & demand variability" sub={`${d.demand.profile} profile · avg ${d.demand.avg_monthly}/mo · CV ${d.demand.cv ?? "—"}`}>
          <div className="h-[190px]">
            <ResponsiveContainer>
              <AreaChart data={d.consumption}>
                <defs>
                  <linearGradient id="cons" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#06b6d4" stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="qty" stroke="#06b6d4" strokeWidth={2} fill="url(#cons)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel testid="part360-supply" className="lg:col-span-4" title="Lead time & vendor performance">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Vendor" value={d.supply.vendor} />
            <Field label="Lead time" value={`${d.supply.lead_time_days} days`} />
            <Field label="OTIF" value={`${d.supply.otif_pct}%`} />
            <Field label="MOQ" value={d.supply.moq} />
            <Field label="Unit price" value={inr(d.supply.unit_price)} />
            <Field label="Last price change" value={d.supply.last_price_change} />
          </div>
        </Panel>

        <Panel testid="part360-operations" className="lg:col-span-4" title="Equipment & line criticality">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Equipment" value={d.operations.equipment} />
            <Field label="Production line" value={d.operations.production_line} />
            <Field label="Criticality" value={d.operations.criticality} />
            <Field label="Downtime cost" value={`${inr(d.operations.downtime_cost_per_hour)}/hr`} />
          </div>
        </Panel>

        <Panel testid="part360-open-supply" className="lg:col-span-4" title="Open POs, inbound & transfers">
          {d.open_supply.length === 0 ? (
            <div className="text-sm text-muted-foreground">No open purchase orders or inbound stock for this material.</div>
          ) : (
            <div className="space-y-2">
              {d.open_supply.map((p) => (
                <div key={p.po_number + p.plant} className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-2.5 text-sm">
                  <span className="font-mono text-xs">PO {p.po_number}</span>
                  <span>{p.plant}</span>
                  <span className="font-mono tabular">{p.qty} {m.uom}</span>
                  <span className="font-mono text-xs text-muted-foreground">ETA {p.eta}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel testid="part360-substitutes" className="lg:col-span-5" title="Alternate / interchangeable parts" sub="Same category, different manufacturer — engineering validation required">
          <div className="space-y-2">
            {d.substitutes.map((s) => (
              <div key={s.material_code} className="rounded-lg border border-border bg-muted/30 p-2.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-mono">{s.description}</span>
                  <span className="font-mono tabular text-xs">{s.qty} @ {s.plant}</span>
                </div>
                <div className="font-mono text-[11px] text-muted-foreground">{s.manufacturer} · {s.specification}</div>
              </div>
            ))}
            {d.substitutes.length === 0 && <div className="text-sm text-muted-foreground">No alternates identified.</div>}
          </div>
        </Panel>

        <Panel testid="part360-recommendation" className="lg:col-span-7" title="Recommended safety-stock level" sub="Explained, not black-box">
          <div className="flex items-center gap-3">
            <Badge variant="outline" className={actionTone(rec.action)}>{rec.action}</Badge>
            <div className="font-display text-2xl font-extrabold tabular">{rec.current_safety_stock} <span className="text-muted-foreground">→</span> {rec.recommended_safety_stock}</div>
            {rec.working_capital_release > 0 && <span className="font-mono text-xs font-semibold text-emerald-500">{inr(rec.working_capital_release)} released</span>}
          </div>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="part360-narrative">{rec.narrative}</p>
          <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
            {rec.reasons.map((x, i) => <li key={i} className="flex gap-2"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-primary" />{x}</li>)}
          </ul>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" data-testid="part360-apply" onClick={() => act(rec.action, `Safety stock ${rec.action}d to ${rec.recommended_safety_stock}`)}>
              <ClipboardCheck className="mr-1.5 h-3.5 w-3.5" />Apply {rec.action}
            </Button>
            <Button size="sm" variant="outline" data-testid="part360-transfer" onClick={() => act("stock_transfer", "Stock transfer initiated")}>
              <ArrowLeftRight className="mr-1.5 h-3.5 w-3.5" />Initiate stock transfer
            </Button>
            <Button size="sm" variant="ghost" data-testid="part360-approval" onClick={() => act("sent_for_approval", "Sent for planner approval")}>
              <Send className="mr-1.5 h-3.5 w-3.5" />Send for planner approval
            </Button>
          </div>
        </Panel>
      </div>
    </div>
  );
}
